import { useState } from "react";
import { useGetList } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown } from "lucide-react";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { ActivitySearch } from "./activityFilters";
import { projectTitle } from "../deals/projectIdentity";
import { format, subDays } from "date-fns";
export function ActivityAdvancedFilters({
  value,
  onChange,
  onReset,
}: {
  value: ActivitySearch;
  onChange: (value: ActivitySearch) => void;
  onReset: () => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [customPeriod, setCustomPeriod] = useState(false);
  const { taskTypes } = useConfigurationContext();
  const [projectSearch, setProjectSearch] = useState("");
  const { data: projects = [] } = useGetList(
    "project_lookup",
    {
      filter: /^\d+$/.test(projectSearch.trim())
        ? { id: Number(projectSearch) }
        : projectSearch
          ? { q: projectSearch }
          : {},
      meta: { includeArchived: true },
      sort: { field: "id", order: "DESC" },
      pagination: { page: 1, perPage: 200 },
    },
    { enabled: open === "Проект", meta: { persist: false } },
  );
  const { data: authors = [] } = useGetList(
    "sales",
    {
      filter: {},
      sort: { field: "first_name", order: "ASC" },
      pagination: { page: 1, perPage: 300 },
    },
    { enabled: open === "Автор", meta: { persist: false } },
  );
  const count = Object.values(value).filter(Boolean).length;
  const choose = (field: keyof ActivitySearch, selected: string) => {
    onChange({ ...value, [field]: selected });
    setOpen(null);
  };
  const options = (
    field: keyof ActivitySearch,
    items: { id: string; label: string }[],
  ) => (
    <div className="max-h-64 overflow-y-auto space-y-1">
      {[{ id: "", label: "Все" }, ...items].map((item) => (
        <button
          type="button"
          key={item.id}
          aria-pressed={value[field] === item.id}
          className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-muted aria-pressed:bg-primary/10"
          onClick={() => choose(field, item.id)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
  const project = projects.find((p) => String(p.id) === value.project);
  const author = authors.find((a) => String(a.id) === value.author);
  const selected = {
    Проект: value.project
      ? project
        ? projectTitle(project)
        : `Проект №${value.project}`
      : "",
    "Тип задачи":
      taskTypes.find((t) => t.value === value.taskType)?.label ??
      value.taskType,
    Автор: value.author
      ? `${author?.first_name ?? "Сотрудник"} ${author?.last_name ?? value.author}`
      : "",
    Период:
      value.from || value.to
        ? `${value.from || "Начало"} — ${value.to || "Сегодня"}`
        : "",
  };
  return (
    <section aria-label="Фильтры событий" className="mb-4 flex flex-wrap gap-2">
      {Object.entries(selected).map(([label, text]) => (
        <Popover
          key={label}
          open={open === label}
          onOpenChange={(active) => setOpen(active ? label : null)}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="max-w-full rounded-full gap-1.5"
              aria-label={label}
            >
              <span className="truncate">
                {label}
                {text ? `: ${text}` : ""}
              </span>
              <ChevronDown className="size-3 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-80 max-w-[calc(100vw-2rem)] rounded-2xl p-3 space-y-2"
          >
            <p className="text-sm font-medium">{label}</p>
            {label === "Проект" && (
              <>
                <Input
                  autoFocus
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  placeholder="Номер или название"
                />
                {options(
                  "project",
                  projects.map((p) => ({
                    id: String(p.id),
                    label: projectTitle(p),
                  })),
                )}
              </>
            )}
            {label === "Автор" &&
              options(
                "author",
                authors.map((a) => ({
                  id: String(a.id),
                  label: `${a.first_name} ${a.last_name}`,
                })),
              )}
            {label === "Тип задачи" &&
              options(
                "taskType",
                taskTypes
                  .filter((t) => t.value && t.value !== "none")
                  .map((t) => ({ id: t.value, label: t.label })),
              )}
            {label === "Период" && (
              <>
                <div className="flex flex-wrap gap-2">
                  {[
                    [0, "Сегодня"],
                    [6, "7 дней"],
                    [29, "30 дней"],
                  ].map(([days, name]) => (
                    <Button
                      key={name}
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        onChange({
                          ...value,
                          from: format(
                            subDays(new Date(), Number(days)),
                            "yyyy-MM-dd",
                          ),
                          to: format(new Date(), "yyyy-MM-dd"),
                        });
                        setOpen(null);
                      }}
                    >
                      {name}
                    </Button>
                  ))}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCustomPeriod(!customPeriod)}
                >
                  Указать даты
                </Button>
                {customPeriod && (
                  <div className="grid grid-cols-2 gap-2">
                    {(["from", "to"] as const).map((field) => (
                      <label className="min-w-0 text-xs" key={field}>
                        {field === "from" ? "С даты" : "По дату"}
                        <Input
                          type="date"
                          aria-label={field === "from" ? "С даты" : "По дату"}
                          value={value[field]}
                          onChange={(e) =>
                            onChange({ ...value, [field]: e.target.value })
                          }
                        />
                      </label>
                    ))}
                  </div>
                )}
                {value.from && value.to && value.from > value.to && (
                  <p role="alert" className="text-xs text-destructive">
                    Начало периода должно быть не позже окончания.
                  </p>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    onChange({ ...value, from: "", to: "" });
                    setOpen(null);
                  }}
                >
                  За всё время
                </Button>
              </>
            )}
          </PopoverContent>
        </Popover>
      ))}
      {count > 0 && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            onReset();
            setProjectSearch("");
            setOpen(null);
          }}
        >
          Сбросить фильтры
        </Button>
      )}
    </section>
  );
}
